import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

const en = {
	common: {
		appName: 'RankPulse',
		loading: 'Loading…',
		signIn: 'Sign in',
		signOut: 'Sign out',
		signUp: 'Create account',
		email: 'Email',
		password: 'Password',
		name: 'Full name',
		organization: 'Organization name',
		slug: 'Slug',
		create: 'Create',
		cancel: 'Cancel',
		retry: 'Retry',
		save: 'Save',
		searchPlaceholder: 'Search…',
		notRanked: 'Not ranked',
		errorTitle: 'Could not load data',
		errorRetry: 'Something went wrong. Refresh the page or try again later.',
	},
	auth: {
		loginTitle: 'Sign in to RankPulse',
		loginSubtitle: 'Use your email and password.',
		registerTitle: 'Create your organization',
		registerSubtitle: 'You will be the owner. Invite teammates later.',
		dontHaveAccount: "Don't have an account?",
		alreadyHaveAccount: 'Already have an account?',
		invalidCredentials: 'Invalid email or password',
	},
	projects: {
		title: 'Projects',
		empty: 'No projects yet',
		emptyDescription: 'Create your first project to start tracking domains, keywords and competitors.',
		newProject: 'New project',
		primaryDomain: 'Primary domain',
		kind: 'Kind',
		create: 'Create project',
		nameLabel: 'Project name',
		domainLabel: 'Primary domain',
		domainHint: 'Bare domain, e.g. controlrondas.com',
	},
	credentials: {
		title: 'Credentials',
		subtitle:
			'Connect your data providers. Credentials can target the org, a portfolio, a project or a specific domain.',
		empty: 'No credentials yet',
		emptyDescription: 'Add a DataForSEO credential to start collecting SERP data.',
		add: 'Add credential',
		provider: 'Provider',
		scope: 'Scope',
		scopeHelp: 'org / portfolio / project / domain',
		label: 'Label',
		secret: 'Secret',
		secretDataForSeoHint: 'Format: email|api_password (DataForSEO Basic auth)',
		registered: 'Credential registered',
		lastFour: 'Ends in',
	},
	rankings: {
		title: 'Rankings',
		subtitle: 'Latest SERP positions across tracked keywords.',
		empty: 'No observations yet',
		emptyDescription: 'Track keywords and schedule a SERP fetch to see positions here.',
		track: 'Track keyword',
		phrase: 'Keyword',
		country: 'Country',
		language: 'Language',
		device: 'Device',
		position: 'Pos.',
		observedAt: 'Observed',
		gscBadge: 'GSC',
		gscHint:
			'Average position from Google Search Console — the live SERP scrape does not rank this domain in the top 100',
	},
	aiSearch: {
		title: 'AI Brand Radar',
		subtitle: 'Prompts monitored across ChatGPT / Claude / Perplexity / Gemini.',
		track: 'Track prompt',
		empty: 'No prompts tracked yet',
		emptyDescription:
			'Add the first prompt you want to monitor across LLM-search providers. Daily captures begin at 07:00 UTC after you connect an OpenAI credential.',
		intent: 'Intent',
		status: 'Status',
		statusActive: 'active',
		statusPaused: 'paused',
		captures: 'Captures',
		capturesEmpty: 'No captures yet',
		capturesEmptyDescription:
			'The first run is scheduled for 07:00 UTC. Connect an OpenAI credential and add at least one location to the project before then.',
		mentionsLabel: 'Mentions',
		citationsLabel: 'Citations',
		confirmDelete: 'Delete this prompt? Captured history will be kept.',
	},
	ga4: {
		listTitle: 'Google Analytics 4',
		listSubtitle: 'Linked GA4 properties.',
		properties: 'Properties',
		link: 'Link property',
		emptyTitle: 'No GA4 property linked',
		emptyDescription:
			'Link a property to pull sessions, users, pageviews and conversions from your real GA4 traffic.',
		handle: 'Property',
		status: 'Status',
		statusActive: 'active',
		statusUnlinked: 'unlinked',
		linkedAt: 'Linked at',
		actions: 'Actions',
		view: 'Traffic',
		trafficTitle: 'GA4 traffic',
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d' },
		empty: {
			title: 'No traffic captured yet',
			description: 'Once GA4 daily fetches run, sessions, users and pageviews appear here.',
		},
		kpi: {
			sessions: 'Sessions',
			sessionsSparkline: 'Daily sessions trend',
			users: 'Users',
			usersSparkline: 'Daily users trend',
			pageviews: 'Pageviews',
			pageviewsSparkline: 'Daily pageviews trend',
			conversions: 'Conversions',
			conversionsSparkline: 'Daily conversions trend',
			revenueHint: 'Revenue {{amount}}',
		},
		chart: { title: 'Daily sessions · users · pageviews' },
		countries: {
			title: 'Countries',
			empty: 'No country dimension yet — confirm the GA4 fetch includes the country dimension.',
		},
		devices: {
			title: 'Devices',
			empty: 'No device dimension yet — confirm the GA4 fetch includes deviceCategory.',
		},
		sources: {
			title: 'Sources',
			empty: 'No source dimension yet — confirm the GA4 fetch includes sessionSource.',
		},
		table: { dimension: 'Source', sessions: 'Sessions', users: 'Users', conversions: 'Conv.' },
	},
	portfolioCompare: {
		back: 'Back to portfolios',
		compare: 'Side-by-side comparison',
		projectsCount: 'projects',
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d' },
		empty: {
			title: 'No projects in this portfolio',
			description: 'Move existing projects under this portfolio (or create one) to start comparing.',
		},
		notFound: { title: 'Portfolio not found', description: 'It may have been deleted.' },
		grid: {
			title: 'Metric grid',
			hint: 'Heatmap-style cells: green = best of the row, red = worst, neutral otherwise.',
			metric: 'Metric',
		},
		rows: {
			sessions: 'Sessions (GA4)',
			users: 'Users (GA4)',
			pageviews: 'Pageviews (GA4)',
			gscClicks: 'Clicks (GSC)',
			gscImpressions: 'Impressions (GSC)',
			gscCtr: 'CTR (GSC)',
			gscAvgPosition: 'Avg position (GSC)',
			avgSerpPosition: 'Avg SERP position (rank-tracking)',
			bingClicks: 'Clicks (Bing)',
		},
		charts: { sessions: 'Sessions over time', clicks: 'GSC clicks over time' },
		best: {
			title: 'Best performer per metric',
			empty: 'Add projects with linked properties to see leaders.',
		},
	},
	scorecard: {
		back: 'Back to project',
		title: 'Weekly scorecard',
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d vs prior 7d', '28d': 'Last 28d vs prior 28d', '90d': 'Last 90d vs prior 90d' },
		verdict: { winning: 'Winning', flat: 'Flat', losing: 'Losing' },
		verdictHint: {
			winning: 'Composite delta {{pct}}% — keep momentum on what changed below.',
			flat: 'Composite delta {{pct}}% — small movements; investigate "what changed".',
			losing: 'Composite delta {{pct}}% — investigate the drops below.',
		},
		kpi: {
			visibility: 'SEO visibility',
			visibilityHint: 'Σ 1/position over {{keywords}} ranked keywords',
			organic: 'Organic sessions',
			organicHint: 'GA4 sessions in window',
			conversions: 'Conversions',
			conversionsHint: 'GA4 conversions in window',
			sov: 'Top-10 share of voice',
			sovHint: '% of top-10 SERPs you occupy vs competitors',
		},
		changes: {
			title: 'What changed',
			hint: 'Top 5 keyword movements + traffic / conversion deltas in the window.',
			improved: '"{{phrase}}" improved (#{{from}} → #{{to}})',
			dropped: '"{{phrase}}" dropped (#{{from}} → #{{to}})',
			organicTraffic: 'Organic traffic {{sign}}{{pct}}% vs previous window',
			conversions: 'Conversions {{sign}}{{pct}}% vs previous window',
			empty: 'No movements in the window',
			emptyHint: 'Either no observations were captured or no metric changed materially.',
		},
		drilldowns: {
			title: 'Drill down',
			rankings: 'Rankings detail',
			competitors: 'Competitors heatmap',
			aiRadar: 'AI Brand Radar',
		},
		common: { open: 'Open' },
	},
	opportunities: {
		back: 'Back to project',
		title: 'Quick wins finder',
		subtitle: 'Keywords ranking #11–#30 sorted by expected ROI.',
		filterLabel: 'Filter',
		filter: {
			all: 'All',
			'quick-win': 'Quick wins',
			'easy-door': 'Easy doors',
			'strategic-bet': 'Strategic bets',
		},
		category: {
			'quick-win': 'Quick win',
			'easy-door': 'Easy door',
			'strategic-bet': 'Strategic bet',
			frontier: 'Frontier',
		},
		kpi: {
			total: 'Opportunities',
			totalHint: 'Keywords #11–#30',
			quickWins: 'Quick wins',
			quickWinsHint: 'Position 11–15',
			easyDoors: 'Easy doors',
			easyDoorsHint: 'Position 16–20',
			strategic: 'Strategic',
			strategicHint: 'Position 21–30',
		},
		table: {
			title: 'Opportunities ({{count}})',
			hint: 'Score = (31 − position) × estimated CTR gain points if you reach top-10.',
			phrase: 'Keyword',
			position: 'Position',
			category: 'Category',
			ctrGain: 'CTR gain',
			score: 'Score',
			context: 'Context',
		},
		empty: {
			title: 'No opportunities yet',
			description: 'Either nothing ranks #11–#30 or no SERP fetch has run yet — schedule one to populate.',
		},
	},
	gapAnalysis: {
		back: 'Back to project',
		title: 'Competitor gap analysis',
		subtitle: 'Keywords your competitors target that you do not, and where they outrank you.',
		empty: {
			title: 'No competitors tracked',
			description: 'Add a competitor first — the gap analysis compares your positions against theirs.',
			cta: 'Manage competitors',
		},
		selector: { title: 'Active competitor', label: 'Choose a competitor' },
		kpi: {
			gap: 'Pure gap',
			gapHint: 'They rank, you do not',
			behind: 'You behind',
			behindHint: 'They rank better than you',
			ahead: 'You ahead',
			aheadHint: 'You rank better than them',
		},
		sections: {
			gap: {
				title: 'Keywords they rank for and you do not',
				hint: 'Pure content gaps — they capture this traffic, you are absent.',
				empty: 'No pure gaps with this competitor.',
			},
			behind: {
				title: 'Keywords where they outrank you',
				hint: 'You both rank but they are higher — page audit candidates.',
				empty: 'You outrank them everywhere they appear.',
			},
			ahead: {
				title: 'Keywords where you lead them',
				hint: 'Defensive watchlist — keep an eye on movements here.',
				empty: 'No keywords where you currently lead.',
			},
		},
		table: {
			phrase: 'Keyword',
			theirs: 'Their pos',
			ours: 'Your pos',
			gap: 'Δ',
			notRanked: 'Not ranked',
			gapNew: 'New gap',
		},
	},
	dailyActions: {
		title: "Today's actions",
		seeAll: 'See all',
		empty: 'Nothing critical today — check back tomorrow.',
		pageTitle: 'Daily action digest',
		pageSubtitle: 'Top 3 things to do today, ranked by urgency.',
		back: 'Back to project',
		comingSoon: 'Email digest @ 09:00 UTC + Done / Snooze / Dismiss controls coming soon.',
		priority: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
		actions: {
			serpDrop: '"{{phrase}}" dropped from #{{from}} to #{{to}} — investigate fast',
			quickWin: 'Push "{{phrase}}" (currently #{{position}}) into the top-10',
			competitorOvertake:
				'A competitor now outranks you on "{{phrase}}" (their #{{theirPos}} vs your #{{ourPos}})',
			ctrAnomaly: '"{{query}}" has {{impressions}} impressions but zero clicks — broken snippet?',
			brandDecay: 'Non-branded clicks dropped {{deltaPct}}% — brand visibility regression',
			lostOpportunity: '"{{query}}" leaves {{lostClicks}} clicks on the table — push to top-3',
			competitorActive: '{{label}} is shipping (activity score {{score}}/100) — review their changes',
			cta: { investigate: 'Investigate', push: 'Plan push', audit: 'Audit competitor' },
		},
	},
	cannibalization: {
		back: 'Back to project',
		title: 'Cannibalization detector',
		subtitle: 'Keywords where two or more of your own domains compete for the same SERP.',
		filterOn: 'Show only problems',
		filterOff: 'Show all',
		kpi: {
			total: 'Tracked keywords',
			totalHint: 'With at least one observation',
			clean: 'Clean',
			cleanHint: 'Only one of your domains ranks',
			acceptable: 'Acceptable',
			acceptableHint: 'Clear leader, separation ≥15 positions',
			problems: 'Problems',
			problemsHint: 'Two or more of your domains close',
		},
		score: { 0: 'Clean', 1: 'Acceptable', 2: 'Cannibalization', 3: 'Heavy cannibalization' },
		table: {
			title: 'Detail ({{count}})',
			hint: 'Score 2+ means two or more of your domains rank close — consider consolidating.',
			keyword: 'Keyword',
			score: 'Score',
			domains: 'Your domains in top-30',
			recommendation: 'Recommendation',
		},
		rec: {
			clean: 'No cannibalization — keep going.',
			acceptable: 'Acceptable spread — monitor.',
			consolidate: 'Consolidate "{{loser}}" into "{{winner}}" with a 301 redirect.',
			specialize: 'Specialize each of your {{count}} domains into distinct intents.',
		},
		empty: {
			noProblems: { title: 'All clean', description: 'No cannibalization in the tracked keywords.' },
			noData: { title: 'No data', description: 'No tracked keywords with observations yet.' },
		},
	},
	competitors: {
		title: 'Competitors',
		subtitleCount: 'tracked competitors',
		add: 'Add competitor',
		noCompetitorsTracked:
			'No competitor domains added yet — the heatmap below shows only your own domains. Add competitors to compare side-by-side.',
		empty: {
			title: 'No competitor data yet',
			description: 'Add a competitor domain and let SERP fetches populate positions to compare side-by-side.',
		},
		kpi: {
			tracked: 'Competitors',
			trackedHint: 'Active in this project',
			keywords: 'Keywords',
			keywordsHint: 'With at least one observation',
			ourRank: 'Your visibility rank',
			ourRankHint: 'of {{total}} domains',
			visibility: 'Your visibility score',
			visibilityHint: 'Σ 1/position over all keywords',
		},
		heatmap: {
			title: 'Position heatmap (latest observation)',
			hint: 'Rows = keywords. Cells = SERP position per domain. Green ≤ top-3, amber ≤ top-10, red below.',
			keyword: 'Keyword',
			you: 'You',
			notRanked: 'Not ranked',
			empty: 'No SERP observations yet',
			emptyHint: 'Schedule a SERP fetch to populate the heatmap.',
		},
		visibility: {
			title: 'Visibility score per domain',
			hint: 'Higher is better. Computed as Σ (1/position) across keywords.',
			empty: 'No domains with observations.',
		},
		winners: {
			title: 'Keywords where you lead',
			empty: 'You are not ahead of any competitor for the tracked keywords yet.',
		},
		losers: {
			title: 'Keywords where competitors lead',
			empty: 'No competitor outranks you for the tracked keywords. Nice.',
		},
		suggestions: {
			title: 'Suggested competitors',
			hint: 'Domains that frequently appear in the top-10 of your tracked keywords.',
			kwCount: '{{kw}} keywords · {{hits}} top-10 hits',
			promote: 'Promote',
		},
	},
	aiRadar: {
		title: 'AI Brand Radar',
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d' },
		manage: 'Manage prompts',
		empty: {
			title: 'No LLM answers captured yet',
			description:
				'Once daily captures run for your brand prompts, AI mentions and citations will populate this dashboard.',
		},
		kpi: {
			totalAnswers: 'Total answers',
			totalAnswersHint: 'Captured in range',
			sov: 'Share of voice',
			sovHint: '{{count}} answers mention own brand',
			citationRate: 'Citation rate',
			citationRateHint: '{{count}} answers cite own URL',
			totalCitations: 'Total citations',
			totalCitationsHint: 'Across all answers in range',
		},
		chart: {
			title: 'Mentions of own brand per provider',
			hint: 'Daily count of answers mentioning your brand, broken down by LLM provider.',
		},
		sov: {
			title: 'Share of voice (own + competitors)',
			empty: 'No mentions yet.',
		},
		sentiment: {
			title: 'Sentiment of own brand',
			hint: 'Distribution of own-brand mentions by sentiment classification.',
			positive: 'Positive',
			neutral: 'Neutral',
			negative: 'Negative',
			mixed: 'Mixed',
			empty: 'No own-brand mentions captured',
			emptyHint: 'Adjust prompts to surface your brand or wait for the next capture cycle.',
		},
		citations: {
			title: 'Most cited domains',
			hint: 'Domains and unique URLs the LLMs cite when answering your prompts.',
			empty: 'No citations captured in range.',
		},
		table: {
			brand: 'Brand',
			answers: 'Answers',
			sov: 'SoV',
			avgPosition: 'Avg pos',
			domain: 'Domain',
			citations: 'Citations',
			uniqueUrls: 'Unique URLs',
		},
		badge: { own: 'You' },
	},
	rankingsBoard: {
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d' },
		tracked: 'Tracked',
		avgPosition: 'Avg position',
		avgHint: 'Across ranked keywords',
		top10: 'In top 10',
		top10Hint: 'of {{total}} tracked',
		top3: 'In top 3',
		top3Hint: 'of {{total}} tracked',
		chartTitle: 'Position over time',
		chartHint: 'Y-axis inverted: #1 at the top. Toggle keywords below.',
		chartEmpty: 'No keywords selected. Pick keywords from the list below.',
		winners: 'Top movers (last 7d)',
		losers: 'Top losers (last 7d)',
		noMovers: 'No significant movement in the last 7 days.',
		tableTitle: 'Latest position per keyword',
		tableHint: 'Click a keyword to plot it on the chart above. Click the phrase to see history.',
		tableEmpty: 'No tracked keywords with observations in the range.',
		rawTitle: 'All observations',
		rawEmpty: 'No observations.',
		delta: 'Δ 7d',
		openSerpMap: 'SERP Map',
	},
	serpMap: {
		title: 'SERP Map',
		subtitle: 'Top-10 of each tracked keyword classified own / competitor / other.',
		backToRankings: 'Back to Rankings',
		asOf: 'as of',
		openUrl: 'Open URL',
		filtersTitle: 'Filters',
		filterByKeyword: 'Filter by keyword',
		filterKeywordPlaceholder: 'e.g. control de rondas',
		filterByClassification: 'Filter by classification',
		filterByCompetitor: 'Highlight competitor',
		filterAllCompetitors: 'All competitors',
		classification: { all: 'All', own: 'Own', competitor: 'Competitor', other: 'Other' },
		noMatch: 'No keyword matches the current filter.',
		noResultsForFilter: 'No results for the current filter.',
		empty: {
			title: 'No SERP snapshots yet',
			description: 'Schedule a DataForSEO SERP fetch from the Schedules page to populate the map.',
		},
		suggestions: {
			title: 'Unregistered domains in your top-10',
			hint: "External domains appearing in your tracked keywords' top-10. Promote them to start tracking.",
			kwCount: '{{kw}} keywords · {{hits}} top-10 hits',
			bestRank: 'best rank #{{rank}}',
			add: 'Add as competitor',
			empty: 'No external domains repeat in your top-10. The competitive landscape is yours.',
		},
	},
	cockpit: {
		title: 'Decision Cockpit',
		subtitle: 'What to do today, where you are losing money, and what to ship next.',
		backToProject: 'Back to project',
		backToCockpit: 'Back to Cockpit',
		openDetail: 'Open detail',
		notRanked: 'Not ranked',
		kpi: {
			tracked: 'Keywords tracked',
			trackedHint: 'Active in this project',
			top10: 'Own in top 10',
			top10Hint: 'of {{total}}',
			top3: 'Own in top 3',
			top3Hint: 'of {{total}}',
			behindCompetitor: 'Behind competitor',
			behindCompetitorHint: 'Keywords where a competitor outranks you',
			lostClicks: 'Lost clicks/month',
			lostClicksHint: 'Projected gain if you reached top-3 across all queries',
			ctrAnomalies: 'CTR anomalies',
			ctrAnomaliesHint: 'Keywords with impressions but zero clicks',
		},
		brandDecay: {
			alertTitle: 'Non-branded clicks dropped week-over-week',
			alertBody:
				'Non-branded organic clicks fell {{pct}}% ({{thisWeek}} vs {{lastWeek}}). Investigate before it compounds.',
		},
		widgets: {
			dailyActions: {
				title: 'Daily Action Digest',
				hint: 'The 3 things to do today — opportunities, alerts, missing data.',
				description: 'Open the daily digest to see your top 3 actions for today.',
			},
			serpMap: {
				title: 'SERP Map',
				hint: 'Top 5 tracked keywords with your current position.',
				empty: 'No SERP snapshots yet',
				emptyDescription: 'Once SERP fetches run, the top of the map appears here.',
			},
			competitorMoat: {
				title: 'Competitor Moat',
				hint: 'How often competitors outrank you in the tracked keyword set.',
				description:
					'A competitor outranks you on {{losses}} keywords right now. Open the heatmap to drill in.',
			},
			aiRadar: {
				title: 'AI Brand Radar SoV',
				hint: 'Project-wide own-brand mention rate over the last 28 days.',
				description: 'See LLM mentions, citations, and competitor SoV trends.',
				windowHint: 'last {{days}} days',
				sparklineAria: 'Daily own-brand mention rate sparkline',
				empty: 'No AI captures yet',
				emptyDescription: 'Add a brand prompt and connect an LLM provider to start capturing.',
			},
			cannibalization: {
				title: 'Cannibalization',
				hint: 'Pairs of pages competing for the same keyword.',
				description: 'See where multiple pages rank for the same query and split clicks.',
			},
			competitorActivity: {
				title: 'Competitor Activity Radar',
				hint: 'Wayback snapshot deltas + backlinks deltas per registered competitor.',
				empty: 'No competitor activity yet',
				emptyDescription:
					'Schedule a Wayback CDX or DataForSEO Backlinks fetch per competitor from the Schedules page.',
			},
			pageExperience: {
				title: 'Page Experience',
				hint: 'Aggregate Lighthouse + Core Web Vitals score across tracked pages.',
				avgScore: 'avg score',
				summary: '{{total}} pages tracked, {{issues}} with issues',
				empty: 'No tracked pages',
				emptyDescription: 'Add pages to track from the Web Performance section.',
			},
			contentGap: {
				title: 'Content Gap Map',
				hint: 'Top keywords your competitor ranks for that you do not.',
				empty: 'No content gaps detected',
				emptyDescription: 'Schedule a Domain Intersection fetch from DataForSEO to populate gaps.',
				emptyNoCompetitors: 'Add competitors first',
				emptyNoCompetitorsDescription: 'Register a competitor in this project before computing content gaps.',
			},
			searchDemandTrend: {
				title: 'Search Demand Trend',
				hint: 'Monthly search volume across the project keyword universe.',
				empty: 'No demand data yet',
				emptyDescription: 'Schedule a DataForSEO Labs ranked-keywords fetch to populate the trend.',
			},
			forecast90d: {
				title: 'Forecast 90d',
				hint: 'Holt-Winters projection of GSC clicks for the next 90 days.',
				empty: 'No forecast available',
				emptyDescription: 'Need at least 14 days of GSC traffic to fit the smoother.',
				vsHistory: 'vs. last 90d',
				observed: 'Observed',
				forecast: 'Forecast',
			},
			lostOpportunity: {
				title: 'Lost Opportunity Score',
				hint: 'Top 5 keywords by projected click gain at top-3.',
				empty: 'No GSC data yet',
				emptyDescription: 'Link a GSC property and let the worker fetch search-analytics rows.',
			},
			quickWinRoi: {
				title: 'Quick-Win ROI',
				hint: 'Pos #11-#30 keywords ranked by ROI score.',
				empty: 'No quick wins yet',
				emptyDescription: 'No keywords currently sit in #11-#30 with material impressions.',
			},
			ctrAnomaly: {
				title: 'CTR Anomaly Detector',
				hint: 'Ranked but zero-click keywords — likely broken snippet or AI Overview takeover.',
				empty: 'No CTR anomalies',
				emptyDescription: 'Every keyword with material impressions is earning clicks. Nice.',
			},
			brandDecay: {
				title: 'Brand vs No-Brand',
				hint: 'WoW comparison of branded vs non-branded clicks.',
				branded: 'Branded',
				nonBranded: 'Non-branded',
				empty: 'No GSC data yet',
				emptyDescription: 'Link a GSC property to compare branded vs non-branded traffic week-over-week.',
			},
		},
		suggestions: {
			title: 'Top competitor suggestions',
			hint: 'External domains pervasive in your top-10 — register them as competitors to track them.',
			empty: 'No suggestions yet — fetch SERP data first.',
			kwHits: '{{kw}} keywords',
		},
		upcoming: {
			title: 'Coming soon',
			hint: 'Sub-issues of the Decision Cockpit epic. Track at github.com/vgpastor/RankPulse/issues/117.',
			soonBadge: 'soon',
			items: {
				pageExperience: 'Page Experience scorecard (PSI + SSL Labs + Mozilla Obs + Schema.org)',
				forecast90d: 'Forecast 90 days organic traffic',
				competitorActivity: 'Competitor Activity Radar (Wayback CDX + backlinks)',
				contentGap: 'Content Gap Map (keywords-for-site without tracked URL)',
				searchDemand: 'Search Demand Trend (DataForSEO history + Google Trends)',
			},
		},
		ctrAnomaliesPage: {
			title: 'CTR Anomalies',
			subtitle:
				'Keywords ranking but earning zero clicks — likely broken snippet, AI Overviews takeover, or canonical drift.',
			tableHint:
				'Each row is a search query that returned ≥ 50 impressions and 0 clicks at position ≤ 30 in the last 28 days.',
			query: 'Query',
			page: 'Page',
			impressions: 'Impressions',
			avgPosition: 'Avg position',
			empty: 'No anomalies in the current window.',
			kpi: {
				count: 'Anomalies',
				countHint: 'Queries with impressions but zero clicks',
				impressionsLost: 'Impressions wasted',
				impressionsLostHint: 'Sum of impressions that returned no click',
			},
		},
		lostOpportunityPage: {
			title: 'Lost Opportunity Score',
			subtitle: 'Click volume left on the table per keyword if you reached the top-3.',
			tableHint:
				'Lost clicks ≈ impressions × (CTR_top3 - CTR_current) using the AWR-2024 position-CTR curve.',
			query: 'Query',
			page: 'Page',
			impressions: 'Impressions',
			currentPosition: 'Current pos',
			ctrJump: 'CTR current → top-3',
			lostClicks: 'Lost clicks',
			empty: 'No opportunities — every tracked query is already at or above target position.',
			kpi: {
				totalLost: 'Total lost clicks',
				totalLostHint: 'Aggregate over the window',
				queries: 'Queries',
				queriesHint: 'With actionable Δ-CTR',
			},
		},
		competitorActivityPage: {
			title: 'Competitor Activity Radar',
			subtitle: 'Wayback snapshot frequency per registered competitor — proxy for "they are shipping".',
			tableTitle: 'Competitor activity',
			tableHint:
				'Activity score is a 0-100 normalised view of the week-over-week Wayback snapshot delta — higher means the competitor is shipping more pages.',
			competitor: 'Competitor',
			wayback: 'Wayback snapshots (Δ)',
			activityScore: 'Activity score',
			noData: 'No data yet',
			empty: 'No competitor activity captured yet',
			emptyDescription:
				'Schedule a Wayback CDX fetch per competitor from the Schedules page to populate this view.',
			kpi: {
				tracked: 'Competitors tracked',
				trackedHint: 'Registered in this project',
				active: 'Active in window',
				activeHint: 'With non-zero activity score',
				maxScore: 'Top score',
				maxScoreHint: 'Most active competitor',
			},
		},
		pageExperiencePage: {
			title: 'Page Experience',
			subtitle: 'Lighthouse + Core Web Vitals score per tracked page (last 30 days).',
			tableTitle: 'Tracked pages',
			tableHint:
				'Performance, LCP, INP, CLS, SEO, accessibility from the latest PageSpeed Insights snapshot. Sorted by performance score ascending — worst pages first.',
			page: 'Page',
			performance: 'Performance',
			seo: 'SEO',
			accessibility: 'A11y',
			status: 'Status',
			statusGood: 'Good',
			statusOk: 'Needs work',
			statusPoor: 'Poor',
			statusUnknown: 'Unknown',
			statusNoData: 'No data',
			empty: 'No tracked pages yet',
			emptyDescription:
				'Track a page in the Web Performance section, then schedule a PageSpeed Insights fetch.',
			kpi: {
				tracked: 'Pages tracked',
				trackedHint: 'In this project',
				avgScore: 'Avg performance',
				avgScoreHint: 'Score 0-100 across pages',
				issues: 'Pages with issues',
				issuesHint: 'Performance score < 50',
			},
		},
		contentGapPage: {
			title: 'Content Gap Map',
			subtitle: 'Keywords your competitor ranks for that you do not — sorted by ROI.',
			filtersTitle: 'Filters',
			ourDomain: 'Our domain',
			competitorDomain: 'Competitor',
			minVolume: 'Min volume',
			tableTitle: 'Keyword gaps',
			tableHint:
				'ROI score = (search volume × CPC) / (keyword difficulty + 1). Higher means better effort-to-reward.',
			keyword: 'Keyword',
			volume: 'Volume',
			cpc: 'CPC',
			difficulty: 'KD',
			theirPosition: 'Their pos',
			ourPosition: 'Our pos',
			roi: 'ROI',
			notRanking: 'not ranking',
			empty: 'No gaps for this pair',
			emptyDescription:
				'Either you already rank for everything they do, or no Domain Intersection has been fetched yet.',
			noCompetitors: 'No competitors registered',
			noCompetitorsDescription: 'Add a competitor in this project before computing content gaps.',
			kpi: {
				gaps: 'Gaps',
				gapsHint: 'Keywords competitor ranks, you do not',
				totalVolume: 'Total volume',
				totalVolumeHint: 'Sum of monthly searches',
				totalRoi: 'Total ROI',
				totalRoiHint: 'Sum of ROI scores',
			},
		},
		searchDemandTrendPage: {
			title: 'Search Demand Trend',
			subtitle: 'Monthly search volume across the project keyword universe (last 13 months).',
			chartTitle: 'Demand trajectory',
			chartHint:
				'Each point sums search_volume across the latest snapshot per (target domain, keyword) in that UTC month.',
			tableTitle: 'Monthly buckets',
			month: 'Month',
			totalVolume: 'Total volume',
			distinctKeywords: 'Distinct keywords',
			empty: 'No demand data yet',
			emptyDescription:
				'Schedule a DataForSEO Labs ranked-keywords fetch from the Schedules page to populate the trend.',
			kpi: {
				latest: 'Latest month',
				latestHint: 'Most recent bucket',
				previous: 'Previous month',
				previousHint: 'One bucket before the latest',
				deltaPct: 'Δ MoM',
				deltaPctHint: 'Latest vs previous, percentage',
			},
		},
		forecast90dPage: {
			title: 'Forecast 90d',
			subtitle: 'Holt-Winters projection of GSC clicks/impressions for the next 90 days.',
			chartTitle: 'Clicks — observed vs forecast',
			chartHint:
				'Solid line: actual GSC clicks for the trailing window. Dashed line: 90-day forward projection from a Holt-Winters smoother (alpha=0.3, beta=0.1, no seasonality).',
			disclaimer:
				'Directional indicator only. Holt-Winters extrapolates trend without weekly/seasonal terms — treat the projection as a "where things are heading", not a forecast you bet on.',
			legend: { observed: 'Observed', forecast: 'Forecast' },
			empty: 'No forecast available',
			emptyDescription:
				'Need at least 14 days of GSC daily traffic to fit the smoother. Schedule a GSC search-analytics fetch from the Schedules page.',
			kpi: {
				history: 'Past 90d clicks',
				historyHint: 'Sum over {{days}} observed days',
				forecast: 'Next 90d clicks',
				forecastHint: 'Holt-Winters forward projection ({{days}} days)',
				delta: 'Δ projected',
				deltaHint: 'Forecast vs history, percentage',
			},
		},
	},
	competitorIntelligence: {
		title: 'Competitor Intelligence',
		subtitle: 'Decision view across keyword gaps, top competitor pages, on-page audit diff and AI mentions.',
		backToProject: 'Back to project',
		filters: {
			ourDomain: 'Our domain',
			competitor: 'Competitor',
			competitorAll: 'All competitors',
			country: 'Country',
			language: 'Language',
			minVolume: 'Min volume',
			dateRange: 'Date range',
		},
		tabs: {
			keywordGaps: 'Keyword gaps',
			topPages: 'Top pages',
			pageAuditsDiff: 'Page audits diff',
			llmMentionTrend: 'LLM mention trend',
			ariaLabel: 'Competitor intelligence sections',
		},
		noCompetitors: 'No competitors registered',
		noCompetitorsDescription: 'Add a competitor in this project before opening competitor intelligence.',
		retry: 'Retry',
		errorTitle: 'Could not load data',
		keywordGaps: {
			title: 'Keyword gaps',
			subtitle: 'Keywords your competitor ranks for that you do not — sorted by ROI.',
			tableHint:
				'ROI score = (search volume × CPC) / (keyword difficulty + 1). Higher means better effort-to-reward.',
			keyword: 'Keyword',
			volume: 'Volume',
			cpc: 'CPC',
			difficulty: 'KD',
			theirPosition: 'Their pos',
			ourPosition: 'Our pos',
			roi: 'ROI',
			notRanking: 'not ranking',
			empty: 'No gaps for this pair',
			emptyDescription:
				'Either you already rank for everything they do, or no Domain Intersection has been fetched yet. "Mark as target" and CSV export are coming soon.',
		},
		topPages: {
			title: 'Top competitor pages',
			subtitle: 'URLs ranking in top 10 for each competitor — aggregated by URL.',
			url: 'URL',
			competitor: 'Competitor',
			keywordsTop10: '# kw top-10',
			totalVolume: 'Total volume',
			avgPosition: 'Avg pos',
			trafficEstimate: 'Traffic est.',
			empty: 'No top-10 pages captured yet',
			emptyDescription: 'Schedule a Ranked Keywords fetch per competitor to populate this view.',
		},
		pageAuditsDiff: {
			title: 'Page audits diff',
			subtitle: 'Compare on-page metrics between your URLs and competitor URLs.',
			pickUrl: 'Competitor URLs',
			pickUrlHint: 'Pick a URL to see the latest audit on the right.',
			ourSideTitle: 'Our URL',
			ourSidePlaceholder:
				'On-page audit for own URLs not available yet. This requires extending web-performance with scope === "own" schedules — tracked as a follow-up. The competitor side is fully populated.',
			ourSideCta: 'Schedule own audit (coming soon)',
			competitorSideTitle: 'Competitor URL',
			selectPrompt: 'Select a competitor URL on the left to load the audit.',
			groupSeoBasics: 'SEO basics',
			groupOnPage: 'On-page metrics',
			groupPerformance: 'Performance',
			groupStructured: 'Structured data',
			fetchedAt: 'Fetched at',
			statusCode: 'Status code',
			pageTitle: 'Title',
			metaDescription: 'Meta description',
			h1: 'H1',
			h2Count: 'H2 count',
			wordCount: 'Word count',
			internalLinks: 'Internal links',
			externalLinks: 'External links',
			canonicalUrl: 'Canonical (differs)',
			lcp: 'LCP',
			cls: 'CLS',
			ttfb: 'TTFB',
			domSize: 'DOM size',
			hasSchema: 'Schema.org',
			schemaTypes: 'Schema types',
			empty: 'No competitor audits captured yet',
			emptyDescription:
				'Schedule a CompetitorPageAudit fetch via providers/.../schedule with systemParams { scope: "competitor", competitorDomain, projectId, url } to populate this view.',
			noUrlSelected: 'Select a URL to view its audit.',
		},
		llmMentionTrend: {
			title: 'LLM mention trend',
			subtitle: 'How often each competitor brand is mentioned across LLM-search providers.',
			rangeLabel: 'Range',
			range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d' },
			chartTitle: 'Daily mentions per competitor (top 5)',
			chartHint:
				'One line per top-5 competitor brand by recent mention count. Aggregated across all providers.',
			tooltipMentions: '{{count}} mentions',
			tooltipSov: '{{pct}}% SoV',
			empty: 'No AI captures in window',
			emptyDescription:
				'Add brand prompts and connect a LLM provider credential before captures appear here.',
		},
	},
	gscPerformance: {
		title: 'GSC performance',
		rangeLabel: 'Range',
		range: { '7d': 'Last 7d', '28d': 'Last 28d', '90d': 'Last 90d', '12mo': 'Last 12mo' },
		empty: {
			title: 'No data yet',
			description: 'Schedule a GSC search-analytics fetch from the Schedules page to populate this view.',
		},
		kpi: {
			clicks: 'Clicks',
			clicksSparkline: 'Daily clicks trend',
			impressions: 'Impressions',
			impressionsSparkline: 'Daily impressions trend',
			ctr: 'CTR',
			ctrSparkline: 'Daily CTR trend',
			avgPosition: 'Avg position',
			avgPositionHint: 'Lower is better',
			positionSparkline: 'Daily avg position trend',
		},
		chart: { title: 'Daily clicks · impressions · CTR · avg position' },
		topQueries: { title: 'Top queries', column: 'Query', empty: 'No queries with data in range.' },
		topPages: { title: 'Top pages', column: 'Page', empty: 'No pages with data in range.' },
		countries: { title: 'Countries', empty: 'No country dimension in the captured data.' },
		devices: { title: 'Devices', empty: 'No device dimension in the captured data.' },
		table: { clicks: 'Clicks', impressions: 'Impr.', ctr: 'CTR', position: 'Pos.' },
	},
	projectKpis: {
		sectionLabel: 'Project key metrics',
		windowHint: 'Last {{days}} days',
		ga4Sessions: 'Sessions (GA4)',
		ga4SessionsSparkline: 'Daily sessions trend',
		ga4Users: 'Users (GA4)',
		ga4UsersSparkline: 'Daily users trend',
		ga4Pageviews: 'Pageviews (GA4)',
		ga4PageviewsSparkline: 'Daily pageviews trend',
		gscClicks: 'Clicks (GSC)',
		gscClicksSparkline: 'Daily GSC clicks trend',
		gscHint: 'CTR {{ctr}}% · Avg pos {{position}}',
		bingClicks: 'Bing clicks',
		bingClicksSparkline: 'Daily Bing clicks trend',
		avgSerpPosition: 'Avg SERP position',
		rankingsHint: '{{count}} tracked observations',
		radarRank: 'Cloudflare Radar rank',
		radarHint: 'Best rank in window',
		radarEmpty: 'Track a domain to populate',
		radarSparkline: 'Daily radar rank trend',
		aiMentions: 'AI prompts tracked',
		aiHint: 'Captured across LLM providers',
	},
} as const;

const es = {
	common: {
		appName: 'RankPulse',
		loading: 'Cargando…',
		signIn: 'Entrar',
		signOut: 'Salir',
		signUp: 'Crear cuenta',
		email: 'Email',
		password: 'Contraseña',
		name: 'Nombre completo',
		organization: 'Nombre de la organización',
		slug: 'Slug',
		create: 'Crear',
		cancel: 'Cancelar',
		retry: 'Reintentar',
		save: 'Guardar',
		searchPlaceholder: 'Buscar…',
		notRanked: 'Sin ranking',
		errorTitle: 'No se han podido cargar los datos',
		errorRetry: 'Algo ha ido mal. Refresca la página o inténtalo más tarde.',
	},
	auth: {
		loginTitle: 'Entra en RankPulse',
		loginSubtitle: 'Usa tu email y contraseña.',
		registerTitle: 'Crea tu organización',
		registerSubtitle: 'Serás el propietario. Podrás invitar a tu equipo más tarde.',
		dontHaveAccount: '¿No tienes cuenta?',
		alreadyHaveAccount: '¿Ya tienes cuenta?',
		invalidCredentials: 'Email o contraseña inválidos',
	},
	projects: {
		title: 'Proyectos',
		empty: 'Aún no hay proyectos',
		emptyDescription: 'Crea tu primer proyecto para empezar a monitorizar dominios, keywords y competidores.',
		newProject: 'Nuevo proyecto',
		primaryDomain: 'Dominio principal',
		kind: 'Tipo',
		create: 'Crear proyecto',
		nameLabel: 'Nombre del proyecto',
		domainLabel: 'Dominio principal',
		domainHint: 'Dominio sin protocolo, ej. controlrondas.com',
	},
	credentials: {
		title: 'Credenciales',
		subtitle:
			'Conecta tus proveedores de datos. Cada credencial puede aplicarse a la organización, una cartera, un proyecto o un dominio.',
		empty: 'Aún no hay credenciales',
		emptyDescription: 'Añade una credencial de DataForSEO para empezar a recopilar datos SERP.',
		add: 'Añadir credencial',
		provider: 'Proveedor',
		scope: 'Alcance',
		scopeHelp: 'org / portfolio / project / domain',
		label: 'Etiqueta',
		secret: 'Secret',
		secretDataForSeoHint: 'Formato: email|api_password (Basic auth de DataForSEO)',
		registered: 'Credencial registrada',
		lastFour: 'Termina en',
	},
	rankings: {
		title: 'Rankings',
		subtitle: 'Últimas posiciones SERP por keyword.',
		empty: 'Sin observaciones aún',
		emptyDescription: 'Trackea keywords y programa un fetch SERP para ver posiciones aquí.',
		track: 'Trackear keyword',
		phrase: 'Keyword',
		country: 'País',
		language: 'Idioma',
		device: 'Dispositivo',
		position: 'Pos.',
		observedAt: 'Observado',
		gscBadge: 'GSC',
		gscHint:
			'Posición media en Google Search Console — el scrape del SERP en vivo no rankea este dominio en el top 100',
	},
	aiSearch: {
		title: 'AI Brand Radar',
		subtitle: 'Prompts monitorizados en ChatGPT / Claude / Perplexity / Gemini.',
		track: 'Trackear prompt',
		empty: 'Aún no hay prompts trackeados',
		emptyDescription:
			'Añade el primer prompt que quieras monitorizar en buscadores LLM. Las capturas diarias empiezan a las 07:00 UTC tras conectar una credencial OpenAI.',
		intent: 'Intent',
		status: 'Estado',
		statusActive: 'activo',
		statusPaused: 'pausado',
		captures: 'Capturas',
		capturesEmpty: 'Aún sin capturas',
		capturesEmptyDescription:
			'La primera ejecución está programada para las 07:00 UTC. Conecta una credencial OpenAI y añade al menos una ubicación al proyecto antes de esa hora.',
		mentionsLabel: 'Menciones',
		citationsLabel: 'Citaciones',
		confirmDelete: '¿Eliminar este prompt? El histórico de capturas se mantendrá.',
	},
	ga4: {
		listTitle: 'Google Analytics 4',
		listSubtitle: 'Propiedades GA4 vinculadas.',
		properties: 'Propiedades',
		link: 'Vincular propiedad',
		emptyTitle: 'Sin propiedad GA4 vinculada',
		emptyDescription:
			'Vincula una propiedad para extraer sesiones, usuarios, páginas vistas y conversiones de tu tráfico real GA4.',
		handle: 'Propiedad',
		status: 'Estado',
		statusActive: 'activa',
		statusUnlinked: 'desvinculada',
		linkedAt: 'Vinculada el',
		actions: 'Acciones',
		view: 'Tráfico',
		trafficTitle: 'Tráfico GA4',
		rangeLabel: 'Rango',
		range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d' },
		empty: {
			title: 'Aún no hay tráfico capturado',
			description:
				'Cuando los fetches diarios de GA4 corran, las sesiones, usuarios y páginas vistas aparecerán aquí.',
		},
		kpi: {
			sessions: 'Sesiones',
			sessionsSparkline: 'Tendencia diaria de sesiones',
			users: 'Usuarios',
			usersSparkline: 'Tendencia diaria de usuarios',
			pageviews: 'Páginas vistas',
			pageviewsSparkline: 'Tendencia diaria de páginas vistas',
			conversions: 'Conversiones',
			conversionsSparkline: 'Tendencia diaria de conversiones',
			revenueHint: 'Ingresos {{amount}}',
		},
		chart: { title: 'Diario: sesiones · usuarios · páginas vistas' },
		countries: { title: 'Países', empty: 'Sin dimensión país — verifica que el fetch GA4 incluye country.' },
		devices: {
			title: 'Dispositivos',
			empty: 'Sin dimensión dispositivo — verifica que el fetch incluye deviceCategory.',
		},
		sources: {
			title: 'Fuentes',
			empty: 'Sin dimensión fuente — verifica que el fetch incluye sessionSource.',
		},
		table: { dimension: 'Fuente', sessions: 'Sesiones', users: 'Usuarios', conversions: 'Conv.' },
	},
	portfolioCompare: {
		back: 'Volver a portfolios',
		compare: 'Comparativa lado a lado',
		projectsCount: 'proyectos',
		rangeLabel: 'Rango',
		range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d' },
		empty: {
			title: 'Este portfolio no tiene proyectos',
			description: 'Mueve proyectos existentes a este portfolio (o crea uno) para empezar a comparar.',
		},
		notFound: { title: 'Portfolio no encontrado', description: 'Puede haber sido eliminado.' },
		grid: {
			title: 'Grid de métricas',
			hint: 'Celdas tipo heatmap: verde = mejor de la fila, rojo = peor, neutro en otros casos.',
			metric: 'Métrica',
		},
		rows: {
			sessions: 'Sesiones (GA4)',
			users: 'Usuarios (GA4)',
			pageviews: 'Páginas vistas (GA4)',
			gscClicks: 'Clicks (GSC)',
			gscImpressions: 'Impresiones (GSC)',
			gscCtr: 'CTR (GSC)',
			gscAvgPosition: 'Pos. media (GSC)',
			avgSerpPosition: 'Pos. media SERP (rank-tracking)',
			bingClicks: 'Clicks (Bing)',
		},
		charts: { sessions: 'Sesiones a lo largo del tiempo', clicks: 'Clicks GSC a lo largo del tiempo' },
		best: {
			title: 'Mejor performer por métrica',
			empty: 'Añade proyectos con propiedades vinculadas para ver líderes.',
		},
	},
	scorecard: {
		back: 'Volver al proyecto',
		title: 'Scorecard semanal',
		rangeLabel: 'Rango',
		range: {
			'7d': 'Últimos 7d vs 7d previos',
			'28d': 'Últimos 28d vs 28d previos',
			'90d': 'Últimos 90d vs 90d previos',
		},
		verdict: { winning: 'Ganando', flat: 'Plano', losing: 'Perdiendo' },
		verdictHint: {
			winning: 'Delta compuesto {{pct}}% — mantén el momentum, mira "qué cambió" abajo.',
			flat: 'Delta compuesto {{pct}}% — pequeños movimientos; revisa "qué cambió".',
			losing: 'Delta compuesto {{pct}}% — investiga las caídas abajo.',
		},
		kpi: {
			visibility: 'Visibilidad SEO',
			visibilityHint: 'Σ 1/posición sobre {{keywords}} keywords',
			organic: 'Sesiones orgánicas',
			organicHint: 'Sesiones GA4 en la ventana',
			conversions: 'Conversiones',
			conversionsHint: 'Conversiones GA4 en la ventana',
			sov: 'Share of voice top-10',
			sovHint: '% de top-10 SERPs que ocupas vs competidores',
		},
		changes: {
			title: 'Qué cambió',
			hint: 'Top 5 movimientos de keyword + deltas de tráfico/conversiones en la ventana.',
			improved: '"{{phrase}}" mejoró (#{{from}} → #{{to}})',
			dropped: '"{{phrase}}" bajó (#{{from}} → #{{to}})',
			organicTraffic: 'Tráfico orgánico {{sign}}{{pct}}% vs ventana anterior',
			conversions: 'Conversiones {{sign}}{{pct}}% vs ventana anterior',
			empty: 'Sin movimientos en la ventana',
			emptyHint: 'O no se han capturado observaciones o ninguna métrica varió de manera relevante.',
		},
		drilldowns: {
			title: 'Drill-down',
			rankings: 'Detalle de rankings',
			competitors: 'Heatmap de competidores',
			aiRadar: 'AI Brand Radar',
		},
		common: { open: 'Abrir' },
	},
	opportunities: {
		back: 'Volver al proyecto',
		title: 'Quick wins',
		subtitle: 'Keywords entre #11–#30 ordenadas por ROI esperado.',
		filterLabel: 'Filtro',
		filter: {
			all: 'Todos',
			'quick-win': 'Quick wins',
			'easy-door': 'Easy doors',
			'strategic-bet': 'Apuestas estratégicas',
		},
		category: {
			'quick-win': 'Quick win',
			'easy-door': 'Puerta fácil',
			'strategic-bet': 'Apuesta estratégica',
			frontier: 'Frontier',
		},
		kpi: {
			total: 'Oportunidades',
			totalHint: 'Keywords #11–#30',
			quickWins: 'Quick wins',
			quickWinsHint: 'Posición 11–15',
			easyDoors: 'Puertas fáciles',
			easyDoorsHint: 'Posición 16–20',
			strategic: 'Estratégicas',
			strategicHint: 'Posición 21–30',
		},
		table: {
			title: 'Oportunidades ({{count}})',
			hint: 'Score = (31 − posición) × ganancia estimada de CTR si llegas a top-10.',
			phrase: 'Keyword',
			position: 'Posición',
			category: 'Categoría',
			ctrGain: 'Ganancia CTR',
			score: 'Score',
			context: 'Contexto',
		},
		empty: {
			title: 'Aún no hay oportunidades',
			description: 'O nada rankea entre #11–#30 o no se ha lanzado un fetch SERP — programa uno para poblar.',
		},
	},
	gapAnalysis: {
		back: 'Volver al proyecto',
		title: 'Análisis de gap competitivo',
		subtitle: 'Keywords que tus competidores trabajan y tú no, y dónde te superan.',
		empty: {
			title: 'Sin competidores trackeados',
			description: 'Añade un competidor primero — el análisis compara tus posiciones con las suyas.',
			cta: 'Gestionar competidores',
		},
		selector: { title: 'Competidor activo', label: 'Elige un competidor' },
		kpi: {
			gap: 'Gap puro',
			gapHint: 'Ellos rankean, tú no',
			behind: 'Vas detrás',
			behindHint: 'Ellos rankean mejor que tú',
			ahead: 'Vas delante',
			aheadHint: 'Tú rankeas mejor que ellos',
		},
		sections: {
			gap: {
				title: 'Keywords que rankean ellos y tú no',
				hint: 'Gaps puros de contenido — ellos se llevan ese tráfico, tú no apareces.',
				empty: 'Sin gaps puros con este competidor.',
			},
			behind: {
				title: 'Keywords donde te superan',
				hint: 'Los dos rankeáis pero ellos están por delante — candidatos a auditoría.',
				empty: 'Les superas en todas las que ellos aparecen.',
			},
			ahead: {
				title: 'Keywords donde lideras',
				hint: 'Watchlist defensiva — vigila los movimientos aquí.',
				empty: 'Sin keywords donde lideres ahora.',
			},
		},
		table: {
			phrase: 'Keyword',
			theirs: 'Pos. ellos',
			ours: 'Pos. tú',
			gap: 'Δ',
			notRanked: 'Sin ranking',
			gapNew: 'Nuevo gap',
		},
	},
	dailyActions: {
		title: 'Acciones de hoy',
		seeAll: 'Ver todas',
		empty: 'Nada crítico hoy — vuelve mañana.',
		pageTitle: 'Resumen diario de acciones',
		pageSubtitle: 'Las 3 cosas más urgentes a hacer hoy.',
		back: 'Volver al proyecto',
		comingSoon: 'Email diario a las 09:00 UTC + Done / Snooze / Dismiss vienen pronto.',
		priority: { critical: 'Crítica', high: 'Alta', medium: 'Media', low: 'Baja' },
		actions: {
			serpDrop: '"{{phrase}}" cayó de #{{from}} a #{{to}} — investiga ya',
			quickWin: 'Empuja "{{phrase}}" (actualmente #{{position}}) al top-10',
			competitorOvertake: 'Un competidor te supera en "{{phrase}}" (su #{{theirPos}} vs tu #{{ourPos}})',
			ctrAnomaly: '"{{query}}" tiene {{impressions}} impresiones y cero clicks — ¿snippet roto?',
			brandDecay: 'Clicks no-brand bajaron {{deltaPct}}% — regresión de visibilidad de marca',
			lostOpportunity: '"{{query}}" deja {{lostClicks}} clicks sobre la mesa — empuja al top-3',
			competitorActive: '{{label}} está moviéndose (activity score {{score}}/100) — revisa sus cambios',
			cta: { investigate: 'Investigar', push: 'Planificar push', audit: 'Auditar competidor' },
		},
	},
	cannibalization: {
		back: 'Volver al proyecto',
		title: 'Detector de canibalización',
		subtitle: 'Keywords donde dos o más de tus dominios compiten por el mismo SERP.',
		filterOn: 'Mostrar solo problemas',
		filterOff: 'Mostrar todas',
		kpi: {
			total: 'Keywords trackeadas',
			totalHint: 'Con al menos una observación',
			clean: 'Limpias',
			cleanHint: 'Solo uno de tus dominios rankea',
			acceptable: 'Aceptables',
			acceptableHint: 'Líder claro, separación ≥15 posiciones',
			problems: 'Problemas',
			problemsHint: 'Dos o más de tus dominios cerca',
		},
		score: { 0: 'Limpia', 1: 'Aceptable', 2: 'Canibalización', 3: 'Canibalización fuerte' },
		table: {
			title: 'Detalle ({{count}})',
			hint: 'Score 2+ significa que dos o más de tus dominios rankean cerca — considera consolidar.',
			keyword: 'Keyword',
			score: 'Score',
			domains: 'Tus dominios en top-30',
			recommendation: 'Recomendación',
		},
		rec: {
			clean: 'Sin canibalización — sigue así.',
			acceptable: 'Spread aceptable — monitoriza.',
			consolidate: 'Consolida "{{loser}}" en "{{winner}}" con una redirect 301.',
			specialize: 'Especializa cada uno de tus {{count}} dominios en intents distintos.',
		},
		empty: {
			noProblems: { title: 'Todo limpio', description: 'Sin canibalización en las keywords trackeadas.' },
			noData: { title: 'Sin datos', description: 'No hay keywords trackeadas con observaciones todavía.' },
		},
	},
	competitors: {
		title: 'Competidores',
		subtitleCount: 'competidores trackeados',
		add: 'Añadir competidor',
		noCompetitorsTracked:
			'Aún no hay dominios competidores — el heatmap solo muestra tus dominios. Añade competidores para comparar lado a lado.',
		empty: {
			title: 'Sin datos de competidores',
			description:
				'Añade un dominio competidor y deja que los fetches SERP pueblen las posiciones para comparar.',
		},
		kpi: {
			tracked: 'Competidores',
			trackedHint: 'Activos en este proyecto',
			keywords: 'Keywords',
			keywordsHint: 'Con al menos una observación',
			ourRank: 'Tu rank de visibilidad',
			ourRankHint: 'de {{total}} dominios',
			visibility: 'Tu visibility score',
			visibilityHint: 'Σ 1/posición sobre todas las keywords',
		},
		heatmap: {
			title: 'Heatmap de posiciones (última observación)',
			hint: 'Filas = keywords. Celdas = posición SERP por dominio. Verde ≤ top-3, ámbar ≤ top-10, rojo por debajo.',
			keyword: 'Keyword',
			you: 'Tú',
			notRanked: 'Sin ranking',
			empty: 'Sin observaciones SERP aún',
			emptyHint: 'Programa un fetch SERP para poblar el heatmap.',
		},
		visibility: {
			title: 'Visibility score por dominio',
			hint: 'Mayor es mejor. Calculado como Σ (1/posición) sobre todas las keywords.',
			empty: 'Ningún dominio con observaciones.',
		},
		winners: {
			title: 'Keywords donde lideras',
			empty: 'Aún no superas a ningún competidor en las keywords trackeadas.',
		},
		losers: {
			title: 'Keywords donde te superan',
			empty: 'Ningún competidor te supera en las keywords trackeadas. Bien.',
		},
		suggestions: {
			title: 'Competidores sugeridos',
			hint: 'Dominios que aparecen frecuentemente en el top-10 de tus keywords trackeadas.',
			kwCount: '{{kw}} keywords · {{hits}} apariciones top-10',
			promote: 'Promocionar',
		},
	},
	aiRadar: {
		title: 'AI Brand Radar',
		rangeLabel: 'Rango',
		range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d' },
		manage: 'Gestionar prompts',
		empty: {
			title: 'Aún no hay respuestas LLM capturadas',
			description:
				'Cuando las capturas diarias corran para tus brand prompts, las menciones y citaciones aparecerán aquí.',
		},
		kpi: {
			totalAnswers: 'Respuestas totales',
			totalAnswersHint: 'Capturadas en el rango',
			sov: 'Share of voice',
			sovHint: '{{count}} respuestas mencionan tu marca',
			citationRate: 'Tasa de citación',
			citationRateHint: '{{count}} respuestas citan una URL propia',
			totalCitations: 'Citaciones totales',
			totalCitationsHint: 'Entre todas las respuestas',
		},
		chart: {
			title: 'Menciones de tu marca por proveedor',
			hint: 'Conteo diario de respuestas que mencionan tu marca, desglosado por proveedor LLM.',
		},
		sov: {
			title: 'Share of voice (tuya + competidores)',
			empty: 'Sin menciones todavía.',
		},
		sentiment: {
			title: 'Sentimiento de tu marca',
			hint: 'Distribución de menciones propias por sentimiento.',
			positive: 'Positivo',
			neutral: 'Neutro',
			negative: 'Negativo',
			mixed: 'Mixto',
			empty: 'Sin menciones de tu marca',
			emptyHint: 'Ajusta los prompts para que tu marca aparezca o espera el próximo ciclo.',
		},
		citations: {
			title: 'Dominios más citados',
			hint: 'Dominios y URLs únicas que citan los LLMs al responder tus prompts.',
			empty: 'Sin citaciones en el rango.',
		},
		table: {
			brand: 'Marca',
			answers: 'Respuestas',
			sov: 'SoV',
			avgPosition: 'Pos. media',
			domain: 'Dominio',
			citations: 'Citaciones',
			uniqueUrls: 'URLs únicas',
		},
		badge: { own: 'Tú' },
	},
	rankingsBoard: {
		rangeLabel: 'Rango',
		range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d' },
		tracked: 'Trackeadas',
		avgPosition: 'Pos. media',
		avgHint: 'Entre keywords con ranking',
		top10: 'En top 10',
		top10Hint: 'de {{total}} trackeadas',
		top3: 'En top 3',
		top3Hint: 'de {{total}} trackeadas',
		chartTitle: 'Posición a lo largo del tiempo',
		chartHint: 'Eje Y invertido: #1 arriba. Activa/desactiva keywords abajo.',
		chartEmpty: 'No hay keywords seleccionadas. Elige keywords en la lista de abajo.',
		winners: 'Top subidas (últimos 7d)',
		losers: 'Top bajadas (últimos 7d)',
		noMovers: 'Sin movimientos significativos en los últimos 7 días.',
		tableTitle: 'Última posición por keyword',
		tableHint: 'Pulsa una keyword para añadirla al gráfico. Pulsa la frase para ver el histórico.',
		tableEmpty: 'No hay keywords con observaciones en el rango.',
		rawTitle: 'Todas las observaciones',
		rawEmpty: 'Sin observaciones.',
		delta: 'Δ 7d',
		openSerpMap: 'SERP Map',
	},
	serpMap: {
		title: 'SERP Map',
		subtitle: 'Top-10 de cada keyword clasificado en propio / competidor / externo.',
		backToRankings: 'Volver a Rankings',
		asOf: 'al',
		openUrl: 'Abrir URL',
		filtersTitle: 'Filtros',
		filterByKeyword: 'Filtrar por keyword',
		filterKeywordPlaceholder: 'p. ej. control de rondas',
		filterByClassification: 'Filtrar por clasificación',
		filterByCompetitor: 'Resaltar competidor',
		filterAllCompetitors: 'Todos los competidores',
		classification: { all: 'Todos', own: 'Propio', competitor: 'Competidor', other: 'Externo' },
		noMatch: 'Ninguna keyword coincide con el filtro actual.',
		noResultsForFilter: 'Sin resultados para el filtro actual.',
		empty: {
			title: 'Aún no hay snapshots SERP',
			description: 'Programa un fetch SERP de DataForSEO desde Schedules para poblar el mapa.',
		},
		suggestions: {
			title: 'Dominios sin registrar en tu top-10',
			hint: 'Dominios externos que aparecen en el top-10 de tus keywords. Promociónalos para empezar a tracking.',
			kwCount: '{{kw}} keywords · {{hits}} apariciones top-10',
			bestRank: 'mejor posición #{{rank}}',
			add: 'Añadir como competidor',
			empty: 'Ningún dominio externo se repite en tu top-10. El terreno competitivo es tuyo.',
		},
	},
	cockpit: {
		title: 'Decision Cockpit',
		subtitle: 'Qué hacer hoy, dónde dejas dinero sobre la mesa, qué publicar después.',
		backToProject: 'Volver al proyecto',
		backToCockpit: 'Volver al Cockpit',
		openDetail: 'Ver detalle',
		notRanked: 'Sin posición',
		kpi: {
			tracked: 'Keywords trackeadas',
			trackedHint: 'Activas en este proyecto',
			top10: 'Propio en top 10',
			top10Hint: 'de {{total}}',
			top3: 'Propio en top 3',
			top3Hint: 'de {{total}}',
			behindCompetitor: 'Por detrás de competidor',
			behindCompetitorHint: 'Keywords donde un competidor te supera',
			lostClicks: 'Clicks perdidos/mes',
			lostClicksHint: 'Ganancia proyectada si llegaras al top-3 en todas las queries',
			ctrAnomalies: 'Anomalías de CTR',
			ctrAnomaliesHint: 'Keywords con impresiones pero sin clicks',
		},
		brandDecay: {
			alertTitle: 'Los clicks no-brand cayeron semana a semana',
			alertBody:
				'Los clicks orgánicos no-brand bajaron {{pct}}% ({{thisWeek}} vs {{lastWeek}}). Investígalo antes de que se agrave.',
		},
		widgets: {
			dailyActions: {
				title: 'Daily Action Digest',
				hint: 'Las 3 acciones a hacer hoy — oportunidades, alertas, datos faltantes.',
				description: 'Abre el digest diario para ver tus 3 acciones del día.',
			},
			serpMap: {
				title: 'SERP Map',
				hint: 'Top 5 keywords trackeadas con tu posición actual.',
				empty: 'Aún no hay snapshots SERP',
				emptyDescription: 'Cuando se ejecuten los fetches SERP, aparecerá la cabecera del mapa aquí.',
			},
			competitorMoat: {
				title: 'Competitor Moat',
				hint: 'Cuántas veces los competidores te superan en tus keywords.',
				description:
					'Un competidor te supera en {{losses}} keywords ahora mismo. Abre el heatmap para profundizar.',
			},
			aiRadar: {
				title: 'AI Brand Radar SoV',
				hint: 'Mention rate propio del proyecto en los últimos 28 días.',
				description: 'Ve menciones de LLM, citas y tendencias SoV de competidores.',
				windowHint: 'últimos {{days}} días',
				sparklineAria: 'Sparkline diaria del mention rate de marca propia',
				empty: 'Aún no hay capturas de AI',
				emptyDescription: 'Añade un brand prompt y conecta un proveedor LLM para empezar a capturar.',
			},
			cannibalization: {
				title: 'Canibalización',
				hint: 'Pares de páginas compitiendo por la misma keyword.',
				description: 'Ve dónde varias páginas posicionan para la misma query y se reparten clicks.',
			},
			competitorActivity: {
				title: 'Competitor Activity Radar',
				hint: 'Delta de snapshots Wayback + delta de backlinks por competidor registrado.',
				empty: 'Aún no hay actividad de competidores',
				emptyDescription:
					'Programa un fetch de Wayback CDX o DataForSEO Backlinks por competidor desde Schedules.',
			},
			pageExperience: {
				title: 'Page Experience',
				hint: 'Score Lighthouse + Core Web Vitals agregado entre páginas trackeadas.',
				avgScore: 'score medio',
				summary: '{{total}} páginas trackeadas, {{issues}} con problemas',
				empty: 'No hay páginas trackeadas',
				emptyDescription: 'Añade páginas desde la sección Web Performance.',
			},
			contentGap: {
				title: 'Content Gap Map',
				hint: 'Keywords donde un competidor posiciona y tú no.',
				empty: 'No se han detectado gaps de contenido',
				emptyDescription: 'Programa un fetch de Domain Intersection en DataForSEO para poblar los gaps.',
				emptyNoCompetitors: 'Añade competidores primero',
				emptyNoCompetitorsDescription:
					'Registra un competidor en este proyecto antes de calcular gaps de contenido.',
			},
			searchDemandTrend: {
				title: 'Search Demand Trend',
				hint: 'Volumen de búsqueda mensual en el universo de keywords del proyecto.',
				empty: 'Aún no hay datos de demanda',
				emptyDescription: 'Programa un fetch de DataForSEO Labs ranked-keywords para poblar la tendencia.',
			},
			forecast90d: {
				title: 'Forecast 90d',
				hint: 'Proyección Holt-Winters de clicks GSC para los próximos 90 días.',
				empty: 'No hay forecast disponible',
				emptyDescription: 'Se necesitan al menos 14 días de tráfico GSC para ajustar el modelo.',
				vsHistory: 'vs. últimos 90d',
				observed: 'Observado',
				forecast: 'Forecast',
			},
			lostOpportunity: {
				title: 'Lost Opportunity Score',
				hint: 'Top 5 keywords por ganancia proyectada de clicks al top-3.',
				empty: 'Aún no hay datos de GSC',
				emptyDescription: 'Conecta una propiedad GSC y deja que el worker recoja filas de search-analytics.',
			},
			quickWinRoi: {
				title: 'Quick-Win ROI',
				hint: 'Keywords #11-#30 ordenadas por score de ROI.',
				empty: 'Aún no hay quick wins',
				emptyDescription: 'Ninguna keyword está actualmente en #11-#30 con impresiones materiales.',
			},
			ctrAnomaly: {
				title: 'Detector de anomalías de CTR',
				hint: 'Keywords posicionadas con cero clicks — snippet roto o AI Overview robando clic.',
				empty: 'Sin anomalías de CTR',
				emptyDescription: 'Toda keyword con impresiones materiales obtiene clicks. Bien.',
			},
			brandDecay: {
				title: 'Brand vs No-Brand',
				hint: 'Comparativa semana a semana de clicks branded vs no-branded.',
				branded: 'Branded',
				nonBranded: 'No-branded',
				empty: 'Aún no hay datos de GSC',
				emptyDescription:
					'Conecta una propiedad GSC para comparar tráfico branded vs no-branded semana a semana.',
			},
		},
		suggestions: {
			title: 'Mejores sugerencias de competidor',
			hint: 'Dominios externos persistentes en tu top-10 — regístralos como competidores para hacer tracking.',
			empty: 'Aún no hay sugerencias — primero ejecuta un fetch SERP.',
			kwHits: '{{kw}} keywords',
		},
		upcoming: {
			title: 'Próximamente',
			hint: 'Sub-issues del épico Decision Cockpit. Sigue el progreso en github.com/vgpastor/RankPulse/issues/117.',
			soonBadge: 'pronto',
			items: {
				pageExperience: 'Scorecard de Page Experience (PSI + SSL Labs + Mozilla Obs + Schema.org)',
				forecast90d: 'Forecast 90 días tráfico orgánico',
				competitorActivity: 'Competitor Activity Radar (Wayback CDX + backlinks)',
				contentGap: 'Content Gap Map (keywords-for-site sin URL trackeada)',
				searchDemand: 'Search Demand Trend (DataForSEO history + Google Trends)',
			},
		},
		ctrAnomaliesPage: {
			title: 'Anomalías de CTR',
			subtitle:
				'Keywords posicionadas pero sin clicks — probablemente snippet roto, AI Overviews o canonical drift.',
			tableHint:
				'Cada fila es una query con ≥ 50 impresiones y 0 clicks en posición ≤ 30 los últimos 28 días.',
			query: 'Query',
			page: 'Página',
			impressions: 'Impresiones',
			avgPosition: 'Pos. media',
			empty: 'Sin anomalías en la ventana actual.',
			kpi: {
				count: 'Anomalías',
				countHint: 'Queries con impresiones pero cero clicks',
				impressionsLost: 'Impresiones desperdiciadas',
				impressionsLostHint: 'Suma de impresiones que no produjeron clic',
			},
		},
		lostOpportunityPage: {
			title: 'Lost Opportunity Score',
			subtitle: 'Volumen de clicks que dejas sobre la mesa por keyword si llegaras al top-3.',
			tableHint:
				'Lost clicks ≈ impresiones × (CTR_top3 - CTR_actual) usando la curva AWR-2024 de CTR por posición.',
			query: 'Query',
			page: 'Página',
			impressions: 'Impresiones',
			currentPosition: 'Pos. actual',
			ctrJump: 'CTR actual → top-3',
			lostClicks: 'Clicks perdidos',
			empty: 'Sin oportunidades — todas las queries están en o por encima de la posición objetivo.',
			kpi: {
				totalLost: 'Total clicks perdidos',
				totalLostHint: 'Agregado en la ventana',
				queries: 'Queries',
				queriesHint: 'Con Δ-CTR accionable',
			},
		},
		competitorActivityPage: {
			title: 'Competitor Activity Radar',
			subtitle: 'Frecuencia de snapshots de Wayback por competidor registrado — proxy de "está publicando".',
			tableTitle: 'Actividad de competidores',
			tableHint:
				'Activity score es una vista normalizada 0-100 del delta semanal de snapshots Wayback — más alto = el competidor está publicando más páginas.',
			competitor: 'Competidor',
			wayback: 'Snapshots Wayback (Δ)',
			activityScore: 'Activity score',
			noData: 'Sin datos aún',
			empty: 'Aún no se ha capturado actividad de competidores',
			emptyDescription:
				'Programa un fetch de Wayback CDX por competidor desde Schedules para poblar esta vista.',
			kpi: {
				tracked: 'Competidores trackeados',
				trackedHint: 'Registrados en este proyecto',
				active: 'Activos en ventana',
				activeHint: 'Con activity score > 0',
				maxScore: 'Score máximo',
				maxScoreHint: 'Competidor más activo',
			},
		},
		pageExperiencePage: {
			title: 'Page Experience',
			subtitle: 'Lighthouse + Core Web Vitals por página trackeada (últimos 30 días).',
			tableTitle: 'Páginas trackeadas',
			tableHint:
				'Performance, LCP, INP, CLS, SEO, accesibilidad de la última snapshot de PageSpeed Insights. Ordenado por score ascendente — peores arriba.',
			page: 'Página',
			performance: 'Performance',
			seo: 'SEO',
			accessibility: 'A11y',
			status: 'Estado',
			statusGood: 'Bien',
			statusOk: 'Mejorable',
			statusPoor: 'Mal',
			statusUnknown: 'Desconocido',
			statusNoData: 'Sin datos',
			empty: 'Sin páginas trackeadas',
			emptyDescription: 'Trackea una página desde Web Performance y programa un fetch de PageSpeed Insights.',
			kpi: {
				tracked: 'Páginas trackeadas',
				trackedHint: 'En este proyecto',
				avgScore: 'Performance media',
				avgScoreHint: 'Score 0-100 entre páginas',
				issues: 'Páginas con problemas',
				issuesHint: 'Score performance < 50',
			},
		},
		contentGapPage: {
			title: 'Content Gap Map',
			subtitle: 'Keywords donde tu competidor posiciona y tú no — ordenadas por ROI.',
			filtersTitle: 'Filtros',
			ourDomain: 'Tu dominio',
			competitorDomain: 'Competidor',
			minVolume: 'Volumen mín.',
			tableTitle: 'Gaps de keywords',
			tableHint:
				'ROI = (volumen × CPC) / (keyword difficulty + 1). Mayor = mejor relación esfuerzo/recompensa.',
			keyword: 'Keyword',
			volume: 'Volumen',
			cpc: 'CPC',
			difficulty: 'KD',
			theirPosition: 'Su pos.',
			ourPosition: 'Tu pos.',
			roi: 'ROI',
			notRanking: 'no posiciona',
			empty: 'Sin gaps para este par',
			emptyDescription: 'O ya posicionas en todo lo suyo, o aún no se ha hecho fetch de Domain Intersection.',
			noCompetitors: 'No hay competidores registrados',
			noCompetitorsDescription: 'Añade un competidor a este proyecto antes de calcular gaps.',
			kpi: {
				gaps: 'Gaps',
				gapsHint: 'Keywords del competidor donde tú no',
				totalVolume: 'Volumen total',
				totalVolumeHint: 'Suma de búsquedas mensuales',
				totalRoi: 'ROI total',
				totalRoiHint: 'Suma de ROI scores',
			},
		},
		searchDemandTrendPage: {
			title: 'Search Demand Trend',
			subtitle: 'Volumen de búsqueda mensual en el universo de keywords del proyecto (últimos 13 meses).',
			chartTitle: 'Trayectoria de demanda',
			chartHint:
				'Cada punto suma search_volume usando la última snapshot por (target domain, keyword) en ese mes UTC.',
			tableTitle: 'Buckets mensuales',
			month: 'Mes',
			totalVolume: 'Volumen total',
			distinctKeywords: 'Keywords distintas',
			empty: 'Aún no hay datos de demanda',
			emptyDescription:
				'Programa un fetch de DataForSEO Labs ranked-keywords desde Schedules para poblar la tendencia.',
			kpi: {
				latest: 'Último mes',
				latestHint: 'Bucket más reciente',
				previous: 'Mes anterior',
				previousHint: 'Bucket previo al último',
				deltaPct: 'Δ MoM',
				deltaPctHint: 'Último vs anterior, porcentaje',
			},
		},
		forecast90dPage: {
			title: 'Forecast 90d',
			subtitle: 'Proyección Holt-Winters de clicks/impresiones GSC para los próximos 90 días.',
			chartTitle: 'Clicks — observado vs forecast',
			chartHint:
				'Línea sólida: clicks GSC reales en la ventana. Línea discontinua: proyección a 90 días con Holt-Winters (alpha=0.3, beta=0.1, sin estacionalidad).',
			disclaimer:
				'Indicador direccional. Holt-Winters extrapola tendencia sin términos semanales/estacionales — usa la proyección como "hacia dónde van las cosas", no como un forecast en el que apostar.',
			legend: { observed: 'Observado', forecast: 'Forecast' },
			empty: 'No hay forecast disponible',
			emptyDescription:
				'Se necesitan al menos 14 días de tráfico diario GSC para ajustar el modelo. Programa un fetch de GSC search-analytics desde Schedules.',
			kpi: {
				history: 'Clicks últimos 90d',
				historyHint: 'Suma sobre {{days}} días observados',
				forecast: 'Clicks próximos 90d',
				forecastHint: 'Proyección Holt-Winters ({{days}} días)',
				delta: 'Δ proyectado',
				deltaHint: 'Forecast vs historial, porcentaje',
			},
		},
	},
	competitorIntelligence: {
		title: 'Competitor Intelligence',
		subtitle:
			'Vista de decisión: gaps de keywords, top pages del competidor, diff de auditoría on-page y menciones IA.',
		backToProject: 'Volver al proyecto',
		filters: {
			ourDomain: 'Tu dominio',
			competitor: 'Competidor',
			competitorAll: 'Todos los competidores',
			country: 'País',
			language: 'Idioma',
			minVolume: 'Volumen mín.',
			dateRange: 'Rango de fechas',
		},
		tabs: {
			keywordGaps: 'Gaps de keywords',
			topPages: 'Top pages',
			pageAuditsDiff: 'Diff de auditorías',
			llmMentionTrend: 'Menciones IA',
			ariaLabel: 'Secciones de competitor intelligence',
		},
		noCompetitors: 'No hay competidores registrados',
		noCompetitorsDescription: 'Añade un competidor antes de abrir competitor intelligence.',
		retry: 'Reintentar',
		errorTitle: 'No se han podido cargar los datos',
		keywordGaps: {
			title: 'Gaps de keywords',
			subtitle: 'Keywords donde tu competidor posiciona y tú no — ordenadas por ROI.',
			tableHint:
				'ROI = (volumen × CPC) / (keyword difficulty + 1). Mayor = mejor relación esfuerzo/recompensa.',
			keyword: 'Keyword',
			volume: 'Volumen',
			cpc: 'CPC',
			difficulty: 'KD',
			theirPosition: 'Su pos.',
			ourPosition: 'Tu pos.',
			roi: 'ROI',
			notRanking: 'no posiciona',
			empty: 'Sin gaps para este par',
			emptyDescription:
				'O ya posicionas en todo lo suyo, o aún no se ha hecho fetch de Domain Intersection. "Marcar como atacar" y exportar CSV llegan más adelante.',
		},
		topPages: {
			title: 'Top pages del competidor',
			subtitle: 'URLs en top 10 por competidor — agregadas por URL.',
			url: 'URL',
			competitor: 'Competidor',
			keywordsTop10: '# kw top-10',
			totalVolume: 'Volumen total',
			avgPosition: 'Pos. media',
			trafficEstimate: 'Tráfico est.',
			empty: 'Aún no hay páginas top-10 capturadas',
			emptyDescription: 'Programa un fetch de Ranked Keywords por competidor para poblar esta vista.',
		},
		pageAuditsDiff: {
			title: 'Diff de auditorías de página',
			subtitle: 'Compara métricas on-page entre tus URLs y URLs del competidor.',
			pickUrl: 'URLs del competidor',
			pickUrlHint: 'Elige una URL para ver la última auditoría a la derecha.',
			ourSideTitle: 'Tu URL',
			ourSidePlaceholder:
				'La auditoría on-page para URLs propias todavía no está disponible. Requiere extender web-performance con schedules de scope === "own" — pendiente como follow-up. El lado del competidor sí está completo.',
			ourSideCta: 'Programar auditoría propia (próximamente)',
			competitorSideTitle: 'URL del competidor',
			selectPrompt: 'Selecciona una URL del competidor a la izquierda para cargar la auditoría.',
			groupSeoBasics: 'SEO básico',
			groupOnPage: 'Métricas on-page',
			groupPerformance: 'Performance',
			groupStructured: 'Datos estructurados',
			fetchedAt: 'Capturado',
			statusCode: 'Status code',
			pageTitle: 'Title',
			metaDescription: 'Meta description',
			h1: 'H1',
			h2Count: 'H2 count',
			wordCount: 'Word count',
			internalLinks: 'Enlaces internos',
			externalLinks: 'Enlaces externos',
			canonicalUrl: 'Canonical (difiere)',
			lcp: 'LCP',
			cls: 'CLS',
			ttfb: 'TTFB',
			domSize: 'DOM size',
			hasSchema: 'Schema.org',
			schemaTypes: 'Tipos schema',
			empty: 'Sin auditorías de competidor capturadas',
			emptyDescription:
				'Programa un fetch de CompetitorPageAudit vía providers/.../schedule con systemParams { scope: "competitor", competitorDomain, projectId, url } para poblar esta vista.',
			noUrlSelected: 'Selecciona una URL para ver su auditoría.',
		},
		llmMentionTrend: {
			title: 'Tendencia de menciones LLM',
			subtitle: 'Frecuencia con la que cada marca competidora es mencionada por proveedores LLM-search.',
			rangeLabel: 'Rango',
			range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d' },
			chartTitle: 'Menciones diarias por competidor (top 5)',
			chartHint: 'Una línea por marca competidora top-5 por menciones recientes. Agregado por proveedor.',
			tooltipMentions: '{{count}} menciones',
			tooltipSov: '{{pct}}% SoV',
			empty: 'Sin capturas de IA en el rango',
			emptyDescription:
				'Añade brand prompts y conecta una credencial de proveedor LLM para que aparezcan capturas aquí.',
		},
	},
	gscPerformance: {
		title: 'Performance GSC',
		rangeLabel: 'Rango',
		range: { '7d': 'Últimos 7d', '28d': 'Últimos 28d', '90d': 'Últimos 90d', '12mo': 'Últimos 12m' },
		empty: {
			title: 'Aún no hay datos',
			description:
				'Programa un fetch de search-analytics de GSC desde la página Schedules para poblar esta vista.',
		},
		kpi: {
			clicks: 'Clicks',
			clicksSparkline: 'Tendencia diaria de clicks',
			impressions: 'Impresiones',
			impressionsSparkline: 'Tendencia diaria de impresiones',
			ctr: 'CTR',
			ctrSparkline: 'Tendencia diaria de CTR',
			avgPosition: 'Pos. media',
			avgPositionHint: 'Menor es mejor',
			positionSparkline: 'Tendencia diaria de posición',
		},
		chart: { title: 'Diario: clicks · impresiones · CTR · posición media' },
		topQueries: { title: 'Top queries', column: 'Query', empty: 'Sin queries con datos en el rango.' },
		topPages: { title: 'Top páginas', column: 'Página', empty: 'Sin páginas con datos en el rango.' },
		countries: { title: 'Países', empty: 'Sin dimensión país en los datos.' },
		devices: { title: 'Dispositivos', empty: 'Sin dimensión dispositivo en los datos.' },
		table: { clicks: 'Clicks', impressions: 'Impr.', ctr: 'CTR', position: 'Pos.' },
	},
	projectKpis: {
		sectionLabel: 'Métricas clave del proyecto',
		windowHint: 'Últimos {{days}} días',
		ga4Sessions: 'Sesiones (GA4)',
		ga4SessionsSparkline: 'Tendencia diaria de sesiones',
		ga4Users: 'Usuarios (GA4)',
		ga4UsersSparkline: 'Tendencia diaria de usuarios',
		ga4Pageviews: 'Páginas vistas (GA4)',
		ga4PageviewsSparkline: 'Tendencia diaria de páginas vistas',
		gscClicks: 'Clicks (GSC)',
		gscClicksSparkline: 'Tendencia diaria de clicks GSC',
		gscHint: 'CTR {{ctr}}% · Pos. media {{position}}',
		bingClicks: 'Clicks Bing',
		bingClicksSparkline: 'Tendencia diaria de clicks Bing',
		avgSerpPosition: 'Posición SERP media',
		rankingsHint: '{{count}} observaciones trackeadas',
		radarRank: 'Rank Cloudflare Radar',
		radarHint: 'Mejor rank en el rango',
		radarEmpty: 'Trackea un dominio para verlo',
		radarSparkline: 'Tendencia diaria del rank',
		aiMentions: 'Prompts IA trackeados',
		aiHint: 'Capturados en proveedores LLM',
	},
} as const;

void i18next
	.use(LanguageDetector)
	.use(initReactI18next)
	.init({
		fallbackLng: 'en',
		supportedLngs: ['en', 'es'],
		ns: [
			'common',
			'auth',
			'projects',
			'credentials',
			'rankings',
			'aiSearch',
			'projectKpis',
			'gscPerformance',
			'ga4',
			'rankingsBoard',
			'aiRadar',
			'competitors',
			'portfolioCompare',
			'scorecard',
			'opportunities',
			'gapAnalysis',
			'dailyActions',
			'cannibalization',
			'competitorIntelligence',
			'cockpit',
			'serpMap',
		],
		defaultNS: 'common',
		resources: { en, es },
		interpolation: { escapeValue: false },
	});

export const i18n = i18next;
